import { useRef, useEffect, useMemo } from 'react';
import { useForm as useFormHook } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers';
import { UseLinkForm, LinkFields, AdEnvironment, Format, Platform, AppOption } from '../../types';
import { validationSchema } from '../../hooks/useForm/validationSchema';
import { AD_ENVIRONMENT_OPTIONS, FORMAT_OPTIONS } from '../../constants';
import { useEffectWithPause } from './useEffectWithPause';

interface UseFormProps {
  editFields: LinkFields | undefined;
  appsOptions: AppOption[];
}

export const useForm = ({ editFields, appsOptions }: UseFormProps): UseLinkForm => {
  const form = useFormHook<LinkFields>({
    defaultValues: {
      platform: Platform.android,
    },
    resolver: yupResolver(validationSchema),
  });
  const { reset, setValue, clearErrors } = form;
  const { isSubmitSuccessful } = form.formState;

  const {
    platform,
    adEnvironment,
    format,
    targetApp,
    targetTracker,
    androidApp,
    iosUniversalLinkPath,
    // set default here because this field is conditional and can be undefined
    // so this can trigger unnecessary effects when switches from undefined to false
    androidDeeplinkPathSameAsIos = false,
  } = form.watch([
    'platform',
    'adEnvironment',
    'format',
    'targetApp',
    'targetTracker',
    'androidApp',
    'iosUniversalLinkPath',
    'androidDeeplinkPathSameAsIos',
  ]);

  const platformRef = useRef<Platform>(platform);
  platformRef.current = platform;

  const formats = getFormats(adEnvironment);
  const selectedFormat = formats.find(option => option.value === format);

  const targetAppOption = targetApp ? appsOptions.find(option => option.value === targetApp) : undefined;
  let androidAppOption = androidApp ? appsOptions.find(option => option.value === androidApp) : undefined;

  // android app can be selected as target android or multi app as well as android app
  if (targetAppOption && [Platform.android, Platform.multi].includes(targetAppOption.platform)) {
    androidAppOption = targetAppOption;
  }

  const targetTrackerOption = targetAppOption?.trackerOptions.find(
    trackerOption => trackerOption.value === targetTracker,
  );

  // if hook receives editFields property
  // all autocompletions on change should be omitted
  const { usePausedEffect } = useEffectWithPause({ enablePause: !!editFields });

  useEffect(() => {
    reset(editFields || {});
  }, [reset, editFields]);

  useEffect(() => {
    if (isSubmitSuccessful) {
      reset({});
    }
  }, [reset, isSubmitSuccessful]);

  // for android platform only tdl format is available,
  // for other platforms format must be cleared if adEnvironment is not selected.
  usePausedEffect(() => {
    if (platform === Platform.android) {
      setValue('format', Format.tdl);
      return;
    }

    if (!selectedFormat) {
      setValue('format', '');
    }
  }, [platform, selectedFormat, setValue]);

  // AdEnvironment selection prefills format with default value
  usePausedEffect(() => {
    if (adEnvironment) {
      setValue('format', getDefaultFormat(adEnvironment), { shouldValidate: true });
    }
  }, [adEnvironment, setValue]);

  // prefill fields on target app change
  usePausedEffect(() => {
    setValue('targetAppPlatform', targetAppOption?.platform);

    if (targetAppOption) {
      setValue('rawUniversalLink', targetAppOption.rawUniversalLink);
      setValue('iosAppScheme', targetAppOption.iosAppScheme);
      clearErrors(['rawUniversalLink', 'iosAppScheme']);
    }
  }, [targetAppOption, setValue, clearErrors]);

  // prefill androidAppScheme on android app change
  usePausedEffect(() => {
    if (androidAppOption) {
      setValue('androidAppScheme', androidAppOption.androidAppScheme);
      clearErrors('androidAppScheme');
    }
  }, [androidAppOption, setValue, clearErrors]);

  // prefill fields on target tracker change
  usePausedEffect(() => {
    const trackerSettings = targetTrackerOption?.settings;
    setValue('campaign', trackerSettings?.campaign || '');
    setValue('adgroup', trackerSettings?.adgroup || '');
    setValue('creative', trackerSettings?.creative || '');
    setValue('label', trackerSettings?.label || '');

    if ([Platform.ios, Platform.multi].includes(platformRef.current)) {
      setValue('idfa', trackerSettings?.idfa || '');
    }

    if ([Platform.android, Platform.multi].includes(platformRef.current)) {
      setValue('gpsAdid', trackerSettings?.gpsAdid || '');
    }
  }, [targetTrackerOption, setValue]);

  // androidDeeplinkPath must be equal iosUniversalLinkPath if 'same as iOS path' checkbox is checked
  usePausedEffect(() => {
    if (androidDeeplinkPathSameAsIos) {
      setValue('androidDeeplinkPath', iosUniversalLinkPath, { shouldValidate: true });
    }
  }, [setValue, iosUniversalLinkPath, androidDeeplinkPathSameAsIos]);

  // androidDeeplinkPath must be cleared if 'same as iOS path' checkbox is unchecked
  usePausedEffect(() => {
    if (!androidDeeplinkPathSameAsIos) {
      setValue('androidDeeplinkPath', '', { shouldValidate: true });
    }
  }, [setValue, androidDeeplinkPathSameAsIos]);

  return {
    form,
    platform,
    adEnvironment,
    formats,
    format,
    // if platform is multi targetAppsOptions must contains both ios and android options
    targetAppsOptions: useMemo(
      () =>
        appsOptions.filter(option =>
          platform === Platform.multi
            ? [Platform.ios, Platform.multi].includes(option.platform)
            : option.platform === platform,
        ),
      [appsOptions, platform],
    ),
    androidAppsOptions: useMemo(() => appsOptions.filter(option => option.platform === Platform.android), [
      appsOptions,
    ]),
    targetAppOption,
    targetTrackerOption,
    androidAppOption,
    iosUniversalLinkPath,
    androidDeeplinkPathSameAsIos,
  };
};

const getDefaultFormat = (adEnvironment: AdEnvironment) => {
  if (adEnvironment === AdEnvironment.email) return Format.jsr;
  if (adEnvironment === AdEnvironment.organicSnapchatChats) return Format.tdl;
  return Format.ulink;
};

const getFormats = (adEnvironment?: AdEnvironment) => {
  const option = AD_ENVIRONMENT_OPTIONS.find(adEnvironmentOption => adEnvironmentOption.value === adEnvironment);
  return option ? FORMAT_OPTIONS.filter(formatOption => option.formats.includes(formatOption.value)) : [];
};
